import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { RewardChip } from '@/features/guide-rewards/components/RewardChip';
import type { RewardApprovalMode, RewardRepeatability, RewardTrigger } from '@/features/guide-rewards/types';
import { adminGrowthApi, adminGrowthKeys, type AdminCampaign, type AdminRewardActivity } from '../api/admin-growth.api';
import { GrowthTable, fmtDate, fmtNum, humanize, useGrowthMutation } from './GrowthShared';

// ─── Activities ─────────────────────────────────────────────────────────────

interface ActivityForm {
  key: string;
  title: string;
  description: string;
  trigger: RewardTrigger;
  credits: string;
  approvalMode: RewardApprovalMode;
  repeatability: RewardRepeatability;
  maxPerGuide: string;
  bookings: string;
  travellerPhotos: string;
  campaignId: string;
  isActive: boolean;
  sortOrder: string;
}

const EMPTY_ACTIVITY: ActivityForm = {
  key: '',
  title: '',
  description: '',
  trigger: 'MILESTONE',
  credits: '0',
  approvalMode: 'AUTO',
  repeatability: 'ONCE',
  maxPerGuide: '',
  bookings: '',
  travellerPhotos: '',
  campaignId: '',
  isActive: true,
  sortOrder: '100',
};

function toForm(a: AdminRewardActivity): ActivityForm {
  return {
    key: a.key,
    title: a.title,
    description: a.description,
    trigger: a.trigger,
    credits: String(a.credits),
    approvalMode: a.approvalMode,
    repeatability: a.repeatability,
    maxPerGuide: a.maxPerGuide ? String(a.maxPerGuide) : '',
    bookings: a.criteria?.bookings ? String(a.criteria.bookings) : '',
    travellerPhotos: a.criteria?.travellerPhotos ? String(a.criteria.travellerPhotos) : '',
    campaignId: a.campaignId ?? '',
    isActive: a.isActive,
    sortOrder: String(a.sortOrder ?? 0),
  };
}

function ActivityDialog({
  open,
  activity,
  campaigns,
  onClose,
}: {
  open: boolean;
  activity: AdminRewardActivity | null;
  campaigns: AdminCampaign[];
  onClose: () => void;
}) {
  const [form, setForm] = useState<ActivityForm>(EMPTY_ACTIVITY);
  useEffect(() => {
    if (open) setForm(activity ? toForm(activity) : EMPTY_ACTIVITY);
  }, [open, activity]);
  const set = <K extends keyof ActivityForm>(key: K, value: ActivityForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  const payload = {
    title: form.title,
    description: form.description,
    credits: Number(form.credits),
    approvalMode: form.approvalMode,
    repeatability: form.trigger === 'REFERRAL' ? ('PER_REFERRAL' as const) : ('ONCE' as const),
    maxPerGuide: form.maxPerGuide ? Number(form.maxPerGuide) : null,
    criteria:
      form.trigger === 'MILESTONE'
        ? { bookings: Number(form.bookings || 0), travellerPhotos: Number(form.travellerPhotos || 0) }
        : {},
    campaignId: form.campaignId || null,
    isActive: form.isActive,
    sortOrder: Number(form.sortOrder || 0),
  };

  const create = useGrowthMutation(() => adminGrowthApi.createActivity({ ...payload, key: form.key.toUpperCase(), trigger: form.trigger }), 'Activity created');
  const update = useGrowthMutation(() => adminGrowthApi.updateActivity(activity!._id, payload), 'Activity saved — new rewards use these settings');
  const saving = create.isPending || update.isPending;
  const save = () => (activity ? update : create).mutate(undefined, { onSuccess: onClose });

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{activity ? `Edit ${activity.key}` : 'New reward activity'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {activity && (
          <Alert severity="info">Changes apply to future rewards only. Rewards already created keep the amount and rules they were created with.</Alert>
        )}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField label="Key" value={form.key} disabled={Boolean(activity)} onChange={(e) => set('key', e.target.value.toUpperCase())} helperText="Permanent id, e.g. BOOKINGS_10" />
          <TextField select label="Trigger" value={form.trigger} disabled={Boolean(activity)} onChange={(e) => set('trigger', e.target.value as RewardTrigger)}>
            <MenuItem value="MILESTONE">Booking / photo milestone</MenuItem>
            <MenuItem value="REFERRAL">Referral approved</MenuItem>
            <MenuItem value="TSHIRT_SELFIE">T-shirt selfie approved</MenuItem>
          </TextField>
        </Box>
        <TextField label="Title (shown to guides)" value={form.title} onChange={(e) => set('title', e.target.value)} />
        <TextField label="Description (shown to guides)" value={form.description} onChange={(e) => set('description', e.target.value)} multiline minRows={2} />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' } }}>
          <TextField label="Credits" type="number" value={form.credits} onChange={(e) => set('credits', e.target.value)} inputProps={{ min: 0 }} />
          <TextField select label="Approval" value={form.approvalMode} onChange={(e) => set('approvalMode', e.target.value as RewardApprovalMode)}>
            <MenuItem value="AUTO">Automatic</MenuItem>
            <MenuItem value="MANUAL">Manual (admin approves)</MenuItem>
          </TextField>
          <TextField label="Max per guide" type="number" value={form.maxPerGuide} onChange={(e) => set('maxPerGuide', e.target.value)} helperText={form.trigger === 'REFERRAL' ? 'Blank = unlimited' : 'One-time activity'} disabled={form.trigger !== 'REFERRAL'} />
        </Box>
        {form.trigger === 'MILESTONE' && (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
            <TextField label="Completed bookings" type="number" value={form.bookings} onChange={(e) => set('bookings', e.target.value)} inputProps={{ min: 0 }} />
            <TextField label="Approved traveller photos" type="number" value={form.travellerPhotos} onChange={(e) => set('travellerPhotos', e.target.value)} inputProps={{ min: 0 }} helperText="One photo per completed trip" />
          </Box>
        )}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' } }}>
          <TextField select label="Campaign" value={form.campaignId} onChange={(e) => set('campaignId', e.target.value)} helperText="Limits the activity to the campaign's dates and budget">
            <MenuItem value="">No campaign (always on)</MenuItem>
            {campaigns.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField label="Sort order" type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
        </Box>
        <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />} label="Active (visible to guides and earning rewards)" />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !form.title || !form.description || (!activity && !form.key)}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function GrowthActivitiesPanel() {
  const activities = useQuery({ queryKey: adminGrowthKeys.activities(), queryFn: adminGrowthApi.activities });
  const campaigns = useQuery({ queryKey: adminGrowthKeys.campaigns(), queryFn: adminGrowthApi.campaigns });
  const [editing, setEditing] = useState<AdminRewardActivity | null>(null);
  const [open, setOpen] = useState(false);
  const toggle = useGrowthMutation((a: AdminRewardActivity) => adminGrowthApi.updateActivity(a._id, { isActive: !a.isActive }), 'Activity updated');
  const campaignName = (id: string | null) => campaigns.data?.find((c) => c._id === id)?.name;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">Reward amounts and rules live here — never in code. Rewards are cumulative: a guide earns every milestone they reach.</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setOpen(true); }}>New activity</Button>
      </Box>
      <GrowthTable
        loading={activities.isLoading}
        error={activities.isError}
        rows={activities.data}
        rowKey={(a) => a._id}
        onRowClick={(a) => { setEditing(a); setOpen(true); }}
        emptyText="No activities yet. Run `npm run seed:growth-rewards` on the server or create one."
        columns={[
          { key: 'title', label: 'Activity', render: (a) => <><Typography fontWeight={600} fontSize="0.875rem">{a.title}</Typography><Typography variant="caption" color="text.secondary">{a.key}</Typography></> },
          { key: 'trigger', label: 'Rule', render: (a) => (a.trigger === 'MILESTONE' ? `${a.criteria.bookings ?? 0} bookings · ${a.criteria.travellerPhotos ?? 0} photos` : humanize(a.trigger)) },
          { key: 'credits', label: 'Credits', align: 'right', render: (a) => <b>{fmtNum(a.credits)}</b> },
          { key: 'approval', label: 'Approval', render: (a) => humanize(a.approvalMode) },
          { key: 'repeat', label: 'Repeat', render: (a) => (a.repeatability === 'PER_REFERRAL' ? `Per referral${a.maxPerGuide ? ` (max ${a.maxPerGuide})` : ''}` : 'Once') },
          { key: 'campaign', label: 'Campaign', render: (a) => campaignName(a.campaignId) ?? '—' },
          {
            key: 'active',
            label: 'Active',
            render: (a) => (
              <Switch
                checked={a.isActive}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggle.mutate(a)}
                inputProps={{ 'aria-label': `${a.isActive ? 'Deactivate' : 'Activate'} ${a.title}` }}
              />
            ),
          },
        ]}
      />
      <ActivityDialog open={open} activity={editing} campaigns={campaigns.data ?? []} onClose={() => setOpen(false)} />
    </>
  );
}

// ─── Campaigns ──────────────────────────────────────────────────────────────

function CampaignDialog({ open, campaign, onClose }: { open: boolean; campaign: AdminCampaign | null; onClose: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [budget, setBudget] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setName(campaign?.name ?? '');
    setDescription(campaign?.description ?? '');
    setStartAt(campaign?.startAt ? dayjs(campaign.startAt).format('YYYY-MM-DD') : '');
    setEndAt(campaign?.endAt ? dayjs(campaign.endAt).format('YYYY-MM-DD') : '');
    setBudget(campaign?.budgetCredits != null ? String(campaign.budgetCredits) : '');
    setIsActive(campaign?.isActive ?? true);
  }, [open, campaign]);

  const payload = {
    name,
    description,
    startAt: startAt ? dayjs(startAt).startOf('day').toISOString() : null,
    endAt: endAt ? dayjs(endAt).endOf('day').toISOString() : null,
    budgetCredits: budget ? Number(budget) : null,
    isActive,
  };
  const create = useGrowthMutation(() => adminGrowthApi.createCampaign(payload), 'Campaign created');
  const update = useGrowthMutation(() => adminGrowthApi.updateCampaign(campaign!._id, payload), 'Campaign saved');
  const saving = create.isPending || update.isPending;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{campaign ? 'Edit campaign' : 'New campaign'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline minRows={2} />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
          <TextField type="date" label="Starts" value={startAt} onChange={(e) => setStartAt(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField type="date" label="Ends" value={endAt} onChange={(e) => setEndAt(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
        <TextField
          type="number"
          label="Budget (credits)"
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          helperText={campaign ? `${fmtNum(campaign.issuedCredits)} already issued. Blank = unlimited.` : 'Blank = unlimited. Approvals stop once the budget is used.'}
        />
        <FormControlLabel control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />} label="Active" />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={saving || name.trim().length < 2} onClick={() => (campaign ? update : create).mutate(undefined, { onSuccess: onClose })}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function GrowthCampaignsPanel() {
  const { data, isLoading, isError } = useQuery({ queryKey: adminGrowthKeys.campaigns(), queryFn: adminGrowthApi.campaigns });
  const [editing, setEditing] = useState<AdminCampaign | null>(null);
  const [open, setOpen] = useState(false);
  const now = dayjs();

  const state = (c: AdminCampaign) => {
    if (!c.isActive) return { label: 'Inactive', tone: 'neutral' as const };
    if (c.startAt && now.isBefore(c.startAt)) return { label: 'Scheduled', tone: 'info' as const };
    if (c.endAt && now.isAfter(c.endAt)) return { label: 'Ended', tone: 'neutral' as const };
    if (c.budgetCredits != null && c.issuedCredits >= c.budgetCredits) return { label: 'Budget used', tone: 'warning' as const };
    return { label: 'Live', tone: 'success' as const };
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">Attach activities to a campaign to give them start/end dates and a credit budget.</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setOpen(true); }}>New campaign</Button>
      </Box>
      <GrowthTable
        loading={isLoading}
        error={isError}
        rows={data}
        rowKey={(c) => c._id}
        onRowClick={(c) => { setEditing(c); setOpen(true); }}
        emptyText="No campaigns. Activities without a campaign are always on."
        columns={[
          { key: 'name', label: 'Campaign', render: (c) => <Typography fontWeight={600} fontSize="0.875rem">{c.name}</Typography> },
          { key: 'state', label: 'State', render: (c) => { const s = state(c); return <RewardChip label={s.label} tone={s.tone} />; } },
          { key: 'dates', label: 'Dates', render: (c) => `${fmtDate(c.startAt)} → ${fmtDate(c.endAt)}` },
          { key: 'budget', label: 'Budget', align: 'right', render: (c) => (c.budgetCredits != null ? fmtNum(c.budgetCredits) : 'Unlimited') },
          { key: 'issued', label: 'Issued', align: 'right', render: (c) => fmtNum(c.issuedCredits) },
        ]}
      />
      <CampaignDialog open={open} campaign={editing} onClose={() => setOpen(false)} />
    </>
  );
}
